import { create } from 'zustand';
import { supabase } from '../lib/supabase';

// ─── CamelCase → snake_case converter ──────────────────────────────────────
export function toSnakeCase(obj) {
  if (Array.isArray(obj)) return obj.map(toSnakeCase);
  if (obj === null || typeof obj !== 'object') return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(/[A-Z]+/g, (match, offset) => (offset > 0 ? '_' : '') + match.toLowerCase());
    result[snakeKey] = value;
  }
  return result;
}

// ─── snake_case → camelCase converter ──────────────────────────────────────
export function toCamelCase(obj) {
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  if (obj === null || typeof obj !== 'object') return obj;
  const result = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z0-9])/g, (_, c) => c.toUpperCase());
    result[camelKey] = value;
  }
  return result;
}

// Helper: insert a row and return the inserted row
async function insertRow(table, data) {
  const snakeData = toSnakeCase(data);
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) return null;
  snakeData.user_id = userId;
  delete snakeData.id;
  const { data: rows, error } = await supabase.from(table).insert(snakeData).select();
  if (error) console.error(`Insert ${table} error:`, error.message);
  return rows?.[0] || null;
}

// Helper: update a row by id
async function updateRow(table, id, updates) {
  const snakeUpdates = toSnakeCase(updates);
  delete snakeUpdates.id;
  delete snakeUpdates.user_id;
  delete snakeUpdates.created_at;
  const { error } = await supabase.from(table).update(snakeUpdates).eq('id', id);
  if (error) console.error(`Update ${table} error:`, error.message);
}

// Helper: delete a row by id
async function deleteRow(table, id) {
  const { error } = await supabase.from(table).delete().eq('id', id);
  if (error) console.error(`Delete ${table} error:`, error.message);
}

// Helper: upsert a row by conflict key (e.g. user_id + month)
async function upsertRow(table, data, conflictColumns = 'user_id,month') {
  const snakeData = toSnakeCase(data);
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) return null;
  snakeData.user_id = userId;
  delete snakeData.id;
  delete snakeData.created_at;
  const { data: rows, error } = await supabase
    .from(table)
    .upsert(snakeData, { onConflict: conflictColumns })
    .select();
  if (error) console.error(`Upsert ${table} error:`, error.message);
  return rows?.[0] || null;
}

// Helper: upsert a single-row config table
async function upsertConfig(table, data) {
  const userId = (await supabase.auth.getUser()).data.user?.id;
  if (!userId) return;
  const snakeData = toSnakeCase(data);
  snakeData.user_id = userId;
  delete snakeData.id;
  delete snakeData.created_at;
  const { data: existing } = await supabase.from(table).select('id').limit(1).single();
  if (existing) {
    const id = existing.id;
    delete snakeData.user_id;
    await supabase.from(table).update(snakeData).eq('id', id);
  } else {
    await supabase.from(table).insert(snakeData);
  }
}

// ─── Zustand Store ──────────────────────────────────────────────────────────
const useStore = create((set, get) => ({
  // Auth
  session: null,
  setSession: (session) => set({ session }),

  // Loading
  loading: true,
  setLoading: (loading) => set({ loading }),

  // Hydrate all data from Supabase
  hydrateStore: (data) => set({ ...data }),

  // ─── Data State (empty defaults, populated from Supabase) ────────────────
  accounts: [],
  creditCards: [],
  investments: [],
  subscriptions: [],
  cashFlowMonthly: [],
  netWorthHistory: [],
  budgetCategories: [],
  transactions: [],
  habits: [],
  habitLog: [],
  dailyAssessments: [],
  tasks: [],
  taskEvents: [],
  dailyPlanner: { month: 'March', mantra: '', quarterlyGoals: [], threeThreeThree: { mustDos: [], shouldDos: [], niceToDos: [] } },
  bucketList: [],
  readingList: [],
  places: [],
  network: [],
  bodyMetrics: [],
  weightLog: [],
  nutritionLog: [],
  workoutLog: [],
  liftTargets: [],
  sleepLog: [],
  dailySummaries: [],
  restingHeartRate: [],
  vo2Max: [],
  activitySummaries: [],
  workoutSessions: [],
  exerciseHistory: [],
  bmiLog: [],
  paycheckSnapshots: [],
  balanceSheetMonthly: [],
  taxes: [],
  calorieTargets: { sedentary: 2075, light: 2377, moderate: 2680, heavy: 2982, goal: 2377, proteinGoal: 180, carbGoal: 250, fatGoal: 70 },
  userSettings: { accountsAsOfDate: new Date().toLocaleDateString('sv-SE'), creditScore: 0, subscriptionTier: 'free', budgetConfig: { rothIra: 0, roth401k: 0, hsa: 0, lifeInsurance: 0, rent: 0, carPayment: 0, brokeragePct: 50, payFrequency: 'semi-monthly', recurringPostings: [], paycheckAccountId: null, paycheckAccountName: 'Checking', paycheckAccountType: 'account' }, hiddenAccounts: [], hiddenCreditCards: [], excludeFromLiquidity: [], autoSnapshotEnabled: true, autoPostingsEnabled: true },

  // ─── Finance Actions ────────────────────────────────────────────────────
  addTransaction: async (tx) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ transactions: [...state.transactions, { ...tx, id: tempId }] }));

    // Update account balances synchronously before any async work to avoid stale reads
    const amt = Number(tx.amount) || 0;
    if (amt > 0) {
      const { accounts, creditCards } = get();
      if (tx.addToAccountId) {
        const acct = accounts.find((a) => a.id === tx.addToAccountId);
        const cc = !acct && creditCards.find((c) => c.id === tx.addToAccountId);
        if (acct) await get().updateAccount(tx.addToAccountId, { balance: (acct.balance || 0) + amt });
        else if (cc) await get().updateCreditCard(tx.addToAccountId, { balance: (cc.balance || 0) + amt });
      }
      if (tx.subtractFromAccountId && tx.subtractFromAccountId !== tx.addToAccountId) {
        const acct = accounts.find((a) => a.id === tx.subtractFromAccountId);
        const cc = !acct && creditCards.find((c) => c.id === tx.subtractFromAccountId);
        if (acct) await get().updateAccount(tx.subtractFromAccountId, { balance: (acct.balance || 0) - amt });
        else if (cc) await get().updateCreditCard(tx.subtractFromAccountId, { balance: (cc.balance || 0) - amt });
      }
      // If same account for both, net the effect (add - subtract = 0, so skip)
    }

    const row = await insertRow('transactions', tx);
    if (row) {
      set((state) => ({
        transactions: state.transactions.map((t) => (t.id === tempId ? { ...t, id: row.id } : t)),
      }));
    }
  },

  updateTransaction: async (id, updates) => {
    set((state) => ({
      transactions: state.transactions.map((t) => (t.id === id ? { ...t, ...updates } : t)),
    }));
    await updateRow('transactions', id, updates);
  },

  removeTransaction: async (id) => {
    set((state) => ({ transactions: state.transactions.filter((t) => t.id !== id) }));
    await deleteRow('transactions', id);
  },

  seedTransactions: async (rows) => {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) return;
    // Double-check DB is empty before seeding
    const { count } = await supabase.from('transactions').select('id', { count: 'exact', head: true });
    if (count > 0) return;
    const snakeRows = rows.map((r) => ({ ...toSnakeCase(r), user_id: userId }));
    // Insert in batches of 100 to avoid payload limits
    const all = [];
    for (let i = 0; i < snakeRows.length; i += 100) {
      const batch = snakeRows.slice(i, i + 100);
      const { data, error } = await supabase.from('transactions').insert(batch).select();
      if (error) { console.error('Seed transactions error:', error.message); continue; }
      if (data) all.push(...data);
    }
    set({ transactions: all.length > 0 ? toCamelCase(all) : rows });
  },

  // ─── Paycheck Snapshot Actions ──────────────────────────────────────────
  addPaycheckSnapshot: async (snapshot) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ paycheckSnapshots: [...state.paycheckSnapshots, { ...snapshot, id: tempId }] }));
    const row = await insertRow('paycheck_snapshots', snapshot);
    if (row) {
      set((state) => ({
        paycheckSnapshots: state.paycheckSnapshots.map((p) => (p.id === tempId ? { ...p, id: row.id } : p)),
      }));
    }
  },

  updatePaycheckSnapshot: async (id, updates) => {
    set((state) => ({
      paycheckSnapshots: state.paycheckSnapshots.map((p) => (p.id === id ? { ...p, ...updates } : p)),
    }));
    await updateRow('paycheck_snapshots', id, updates);
  },

  removePaycheckSnapshot: async (id) => {
    set((state) => ({ paycheckSnapshots: state.paycheckSnapshots.filter((p) => p.id !== id) }));
    await deleteRow('paycheck_snapshots', id);
  },

  addAccount: async (account) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ accounts: [...state.accounts, { ...account, id: tempId }] }));
    const row = await insertRow('accounts', account);
    if (row) {
      set((state) => ({ accounts: state.accounts.map((a) => (a.id === tempId ? { ...a, id: row.id } : a)) }));
    }
  },

  updateAccount: async (id, updates) => {
    const existing = get().accounts.find((a) => a.id === id);
    let next = updates;
    if (existing?.frozen) {
      const { balance, asOfDate, ...rest } = updates;
      next = rest;
      if (Object.keys(next).length === 0) return;
    }
    set((state) => ({
      accounts: state.accounts.map((a) => (a.id === id ? { ...a, ...next } : a)),
    }));
    await updateRow('accounts', id, next);
  },

  removeAccount: async (id) => {
    set((state) => ({ accounts: state.accounts.filter((a) => a.id !== id) }));
    await deleteRow('accounts', id);
  },

  addCreditCard: async (card) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ creditCards: [...state.creditCards, { ...card, id: tempId }] }));
    const row = await insertRow('credit_cards', card);
    if (row) {
      set((state) => ({ creditCards: state.creditCards.map((c) => (c.id === tempId ? { ...c, id: row.id } : c)) }));
    }
  },

  removeCreditCard: async (id) => {
    set((state) => ({ creditCards: state.creditCards.filter((c) => c.id !== id) }));
    await deleteRow('credit_cards', id);
  },

  addInvestment: async (investment) => {
    const tempId = crypto.randomUUID();
    const computed = {
      ...investment,
      currentValue: investment.shares * investment.currentPrice,
      gainLoss: (investment.shares * investment.currentPrice) - (investment.shares * investment.costBasis),
      gainLossPercent: investment.costBasis !== 0 ? (((investment.currentPrice - investment.costBasis) / investment.costBasis) * 100) : 0,
      annualDividend: (investment.shares * investment.currentPrice) * (investment.yieldRate / 100),
      yield: investment.yieldRate,
    };
    set((state) => ({ investments: [...state.investments, { ...computed, id: tempId }] }));
    const row = await insertRow('investments', investment);
    if (row) {
      set((state) => ({ investments: state.investments.map((i) => (i.id === tempId ? { ...i, id: row.id } : i)) }));
    }
  },

  removeInvestment: async (id) => {
    set((state) => ({ investments: state.investments.filter((i) => i.id !== id) }));
    await deleteRow('investments', id);
  },

  addBudgetCategory: async (category) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ budgetCategories: [...state.budgetCategories, { ...category, id: tempId }] }));
    const row = await insertRow('budget_categories', category);
    if (row) {
      set((state) => ({ budgetCategories: state.budgetCategories.map((b) => (b.id === tempId ? { ...b, id: row.id } : b)) }));
    }
  },

  removeBudgetCategory: async (id) => {
    set((state) => ({ budgetCategories: state.budgetCategories.filter((b) => b.id !== id) }));
    await deleteRow('budget_categories', id);
  },

  removeNetWorthSnapshot: async (id) => {
    set((state) => ({ netWorthHistory: state.netWorthHistory.filter((n) => n.id !== id) }));
    await deleteRow('net_worth_history', id);
  },

  addCashFlowMonthly: async (entry) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ cashFlowMonthly: [...state.cashFlowMonthly, { ...entry, id: tempId }] }));
    const row = await insertRow('cash_flow_monthly', entry);
    if (row) {
      set((state) => ({ cashFlowMonthly: state.cashFlowMonthly.map((c) => (c.id === tempId ? { ...c, id: row.id } : c)) }));
    }
  },

  updateCashFlowMonthly: async (id, updates) => {
    set((state) => ({
      cashFlowMonthly: state.cashFlowMonthly.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    }));
    await updateRow('cash_flow_monthly', id, updates);
  },

  removeCashFlowMonthly: async (id) => {
    set((state) => ({ cashFlowMonthly: state.cashFlowMonthly.filter((c) => c.id !== id) }));
    await deleteRow('cash_flow_monthly', id);
  },

  seedCashFlowMonthly: async (rows) => {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) return;
    const { count } = await supabase.from('cash_flow_monthly').select('id', { count: 'exact', head: true });
    if (count > 0) return;
    const snakeRows = rows.map((r) => ({ ...toSnakeCase(r), user_id: userId }));
    const { data, error } = await supabase.from('cash_flow_monthly').insert(snakeRows).select();
    if (error) { console.error('Seed cash_flow_monthly error:', error.message); return; }
    set({ cashFlowMonthly: data ? toCamelCase(data) : rows });
  },

  addSubscription: async (sub) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ subscriptions: [...state.subscriptions, { ...sub, id: tempId }] }));
    const row = await insertRow('subscriptions', sub);
    if (row) {
      set((state) => ({
        subscriptions: state.subscriptions.map((s) => (s.id === tempId ? { ...s, id: row.id } : s)),
      }));
    }
  },

  removeSubscription: async (id) => {
    set((state) => ({ subscriptions: state.subscriptions.filter((s) => s.id !== id) }));
    await deleteRow('subscriptions', id);
  },

  updateSubscription: async (id, updates) => {
    set((state) => ({
      subscriptions: state.subscriptions.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    }));
    await updateRow('subscriptions', id, updates);
  },

  seedSubscriptions: async (rows) => {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) return;
    const snakeRows = rows.map((r) => ({ ...toSnakeCase(r), user_id: userId }));
    const { data: inserted, error } = await supabase.from('subscriptions').insert(snakeRows).select();
    if (error) console.error('Seed subscriptions error:', error.message);
    set({ subscriptions: inserted ? toCamelCase(inserted) : rows });
  },

  updateCreditCard: async (id, updates) => {
    set((state) => ({
      creditCards: state.creditCards.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    }));
    await updateRow('credit_cards', id, updates);
  },

  updateInvestment: async (id, updates) => {
    set((state) => ({
      investments: state.investments.map((i) => {
        if (i.id !== id) return i;
        const merged = { ...i, ...updates };
        // Recompute derived fields whenever shares, costBasis, currentPrice, or yieldRate change
        const currentValue = merged.shares * merged.currentPrice;
        const totalCost = merged.shares * merged.costBasis;
        const gainLoss = currentValue - totalCost;
        const gainLossPercent = totalCost !== 0 ? (gainLoss / totalCost) * 100 : 0;
        const annualDividend = currentValue * ((merged.yieldRate || 0) / 100);
        return {
          ...merged,
          currentValue,
          gainLoss,
          gainLossPercent,
          annualDividend,
          yield: merged.yieldRate || 0,
        };
      }),
    }));
    await updateRow('investments', id, updates);
  },

  updateBudgetCategory: async (id, updates) => {
    set((state) => ({
      budgetCategories: state.budgetCategories.map((b) => (b.id === id ? { ...b, ...updates } : b)),
    }));
    await updateRow('budget_categories', id, updates);
  },

  addNetWorthSnapshot: async (snapshot) => {
    set((state) => ({ netWorthHistory: [...state.netWorthHistory, snapshot] }));
    await insertRow('net_worth_history', snapshot);
  },

  // ─── Balance Sheet Monthly Actions ─────────────────────────────────────
  seedBalanceSheetHistory: async (rows) => {
    // Batch upsert all seed rows to DB, then set store with the camelCase input rows
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) return;
    const snakeRows = rows.map((r) => ({ ...toSnakeCase(r), user_id: userId }));
    const { error } = await supabase
      .from('balance_sheet_monthly')
      .upsert(snakeRows, { onConflict: 'user_id,month' });
    if (error) {
      console.error('Seed balance_sheet_monthly error:', error.message);
      return;
    }
    // Store the camelCase rows directly (they came from our own seed data)
    set({ balanceSheetMonthly: rows });
  },

  upsertBalanceSheetMonth: async (entry) => {
    // Optimistic: update or append in store
    set((state) => {
      const idx = state.balanceSheetMonthly.findIndex((r) => r.month === entry.month);
      if (idx >= 0) {
        const updated = [...state.balanceSheetMonthly];
        updated[idx] = { ...updated[idx], ...entry };
        return { balanceSheetMonthly: updated };
      }
      return { balanceSheetMonthly: [...state.balanceSheetMonthly, entry] };
    });
    // Sync to DB
    await upsertRow('balance_sheet_monthly', entry, 'user_id,month');
  },

  // ─── Tax Actions ────────────────────────────────────────────────────────
  upsertTaxYear: async (entry) => {
    set((state) => {
      const idx = state.taxes.findIndex((t) => t.year === entry.year);
      if (idx >= 0) {
        const updated = [...state.taxes];
        updated[idx] = { ...updated[idx], ...entry };
        return { taxes: updated };
      }
      return { taxes: [...state.taxes, entry] };
    });
    await upsertRow('taxes', entry, 'user_id,year');
  },

  seedTaxes: async (rows) => {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) return;
    const snakeRows = rows.map((r) => ({ ...toSnakeCase(r), user_id: userId }));
    const { data: inserted, error } = await supabase
      .from('taxes')
      .upsert(snakeRows, { onConflict: 'user_id,year' })
      .select();
    if (error) console.error('Seed taxes error:', error.message);
    set({ taxes: inserted ? toCamelCase(inserted) : rows });
  },

  // ─── User Settings ──────────────────────────────────────────────────────
  updateUserSettings: async (updates) => {
    set((state) => ({ userSettings: { ...state.userSettings, ...updates } }));
    if (!supabase) return;
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) return;
    const snakeUpdates = toSnakeCase(updates);
    delete snakeUpdates.id;
    delete snakeUpdates.created_at;
    const { data: existing } = await supabase.from('user_settings').select('id').limit(1).single();
    if (existing) {
      const { error } = await supabase.from('user_settings').update(snakeUpdates).eq('id', existing.id);
      if (error) console.error('Update user_settings error:', error.message);
    } else {
      const merged = { ...get().userSettings, ...updates };
      await upsertConfig('user_settings', merged);
    }
  },

  updateBudgetConfig: async (updates) => {
    const newConfig = { ...get().userSettings.budgetConfig, ...updates };
    set((state) => ({ userSettings: { ...state.userSettings, budgetConfig: newConfig } }));
    if (!supabase) return;
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) return;
    const snakeUpdates = toSnakeCase({ budgetConfig: newConfig });
    const { data: existing } = await supabase.from('user_settings').select('id').limit(1).single();
    if (existing) {
      const { error } = await supabase.from('user_settings').update(snakeUpdates).eq('id', existing.id);
      if (error) console.error('Update user_settings error:', error.message);
    } else {
      const merged = { ...get().userSettings, budgetConfig: newConfig };
      await upsertConfig('user_settings', merged);
    }
  },

  // ─── Habit Actions ──────────────────────────────────────────────────────
  toggleHabitLog: async (habitId, date) => {
    const state = get();
    const existing = state.habitLog.find((l) => l.habitId === habitId && l.date === date);

    if (existing) {
      const updated = { ...existing, completed: !existing.completed, score: existing.completed ? 0 : 1 };
      set((s) => ({
        habitLog: s.habitLog.map((l) => (l.id === existing.id ? updated : l)),
      }));
      await updateRow('habit_log', existing.id, { completed: updated.completed, score: updated.score });
    } else {
      const tempId = crypto.randomUUID();
      const newEntry = { id: tempId, habitId, date, completed: true, score: 1 };
      set((s) => ({ habitLog: [...s.habitLog, newEntry] }));
      const row = await insertRow('habit_log', { habitId, date, completed: true, score: 1 });
      if (row) {
        set((s) => ({
          habitLog: s.habitLog.map((l) => (l.id === tempId ? { ...l, id: row.id } : l)),
        }));
      }
    }
  },

  // ─── Task Actions ──────────────────────────────────────────────────────
  addTask: async (task) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ tasks: [...state.tasks, { ...task, id: tempId }] }));
    const row = await insertRow('tasks', task);
    if (row) {
      set((state) => ({ tasks: state.tasks.map((t) => (t.id === tempId ? { ...t, id: row.id } : t)) }));
    }
  },

  updateTask: async (id, updates) => {
    set((state) => ({ tasks: state.tasks.map((t) => (t.id === id ? { ...t, ...updates } : t)) }));
    await updateRow('tasks', id, updates);
  },

  removeTask: async (id) => {
    set((state) => ({ tasks: state.tasks.filter((t) => t.id !== id) }));
    await deleteRow('tasks', id);
  },

  completeTask: async (id) => {
    const now = new Date().toISOString();
    const state = get();
    const task = state.tasks.find((t) => t.id === id);

    // Mark current task as completed
    set((s) => ({
      tasks: s.tasks.map((t) => (t.id === id ? { ...t, status: 'completed', completedAt: now } : t)),
    }));
    await updateRow('tasks', id, { status: 'completed', completedAt: now });

    // If recurring, create next occurrence
    if (task?.isRecurring && task.recurrenceRule) {
      try {
        const { RRule } = await import('rrule');
        const rule = RRule.fromString(task.recurrenceRule);
        const after = task.dueDate ? new Date(task.dueDate + 'T00:00:00') : new Date();
        const nextDates = rule.after(after, false);
        const nextDueDate = nextDates ? nextDates.toLocaleDateString('sv-SE') : null;

        if (nextDueDate) {
          const nextTask = {
            title: task.title,
            notes: task.notes,
            category: task.category,
            priority: task.priority,
            status: 'open',
            dueDate: nextDueDate,
            isRecurring: true,
            recurrenceRule: task.recurrenceRule,
            nextDueDate: null,
            carryForwardCount: 0,
            carryForwardFrom: id,
          };
          const tempId = crypto.randomUUID();
          set((s) => ({ tasks: [...s.tasks, { ...nextTask, id: tempId }] }));
          const row = await insertRow('tasks', nextTask);
          if (row) {
            set((s) => ({ tasks: s.tasks.map((t) => (t.id === tempId ? { ...t, id: row.id } : t)) }));
          }
        }
      } catch (err) {
        console.error('Failed to create next recurring task:', err);
      }
    }
  },

  snoozeTask: async (id, days = 1) => {
    const state = get();
    const task = state.tasks.find((t) => t.id === id);
    if (!task) return;
    const currentDue = task.dueDate ? new Date(task.dueDate + 'T00:00:00') : new Date();
    currentDue.setDate(currentDue.getDate() + days);
    const newDueDate = currentDue.toLocaleDateString('sv-SE');
    set((s) => ({
      tasks: s.tasks.map((t) => (t.id === id ? { ...t, dueDate: newDueDate, status: 'snoozed' } : t)),
    }));
    await updateRow('tasks', id, { dueDate: newDueDate, status: 'snoozed' });
    // Write snooze event (app-side per hybrid approach)
    const eventRow = await insertRow('task_events', { taskId: id, eventType: 'snoozed', eventDate: new Date().toLocaleDateString('sv-SE'), note: `Snoozed ${days} day(s)` });
    if (eventRow) {
      set((s) => ({ taskEvents: [...s.taskEvents, eventRow] }));
    }
  },

  reopenTask: async (id) => {
    set((state) => ({
      tasks: state.tasks.map((t) => (t.id === id ? { ...t, status: 'open', completedAt: null } : t)),
    }));
    await updateRow('tasks', id, { status: 'open', completedAt: null });
  },

  // ─── Daily Assessment Actions ───────────────────────────────────────────
  addDailyAssessment: async (assessment) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ dailyAssessments: [...state.dailyAssessments, { ...assessment, id: tempId }] }));
    const row = await insertRow('daily_assessments', assessment);
    if (row) {
      set((state) => ({
        dailyAssessments: state.dailyAssessments.map((a) => (a.id === tempId ? { ...a, id: row.id } : a)),
      }));
    }
  },

  updateDailyAssessment: async (id, updates) => {
    set((state) => ({
      dailyAssessments: state.dailyAssessments.map((a) => (a.id === id ? { ...a, ...updates } : a)),
    }));
    await updateRow('daily_assessments', id, updates);
  },

  // ─── Planner Actions ────────────────────────────────────────────────────
  updatePlannerMantra: async (mantra) => {
    set((state) => ({ dailyPlanner: { ...state.dailyPlanner, mantra } }));
    await upsertConfig('daily_planner', { ...get().dailyPlanner, mantra });
  },

  updateQuarterlyGoal: async (id, updates) => {
    const planner = get().dailyPlanner;
    const quarterlyGoals = planner.quarterlyGoals.map((g) =>
      g.id === id ? { ...g, ...updates } : g
    );
    set((state) => ({ dailyPlanner: { ...state.dailyPlanner, quarterlyGoals } }));
    await upsertConfig('daily_planner', { ...planner, quarterlyGoals });
  },

  // ─── Bucket List Actions ────────────────────────────────────────────────
  addBucketItem: async (item) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ bucketList: [...state.bucketList, { ...item, id: tempId }] }));
    const row = await insertRow('bucket_list', item);
    if (row) {
      set((state) => ({ bucketList: state.bucketList.map((b) => (b.id === tempId ? { ...b, id: row.id } : b)) }));
    }
  },

  updateBucketItem: async (id, updates) => {
    set((state) => ({ bucketList: state.bucketList.map((b) => (b.id === id ? { ...b, ...updates } : b)) }));
    await updateRow('bucket_list', id, updates);
  },

  removeBucketItem: async (id) => {
    set((state) => ({ bucketList: state.bucketList.filter((b) => b.id !== id) }));
    await deleteRow('bucket_list', id);
  },

  // ─── Reading List Actions ───────────────────────────────────────────────
  addBook: async (book) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ readingList: [...state.readingList, { ...book, id: tempId }] }));
    const row = await insertRow('reading_list', book);
    if (row) {
      set((state) => ({ readingList: state.readingList.map((b) => (b.id === tempId ? { ...b, id: row.id } : b)) }));
    }
  },

  updateBook: async (id, updates) => {
    set((state) => ({ readingList: state.readingList.map((b) => (b.id === id ? { ...b, ...updates } : b)) }));
    await updateRow('reading_list', id, updates);
  },

  removeBook: async (id) => {
    set((state) => ({ readingList: state.readingList.filter((b) => b.id !== id) }));
    await deleteRow('reading_list', id);
  },

  // ─── Places Actions ─────────────────────────────────────────────────────
  updatePlace: async (id, updates) => {
    set((state) => ({ places: state.places.map((p) => (p.id === id ? { ...p, ...updates } : p)) }));
    await updateRow('places', id, updates);
  },

  // ─── Network Actions ────────────────────────────────────────────────────
  addContact: async (contact) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ network: [...state.network, { ...contact, id: tempId }] }));
    const row = await insertRow('network', contact);
    if (row) {
      set((state) => ({ network: state.network.map((n) => (n.id === tempId ? { ...n, id: row.id } : n)) }));
    }
  },

  updateContact: async (id, updates) => {
    set((state) => ({ network: state.network.map((n) => (n.id === id ? { ...n, ...updates } : n)) }));
    await updateRow('network', id, updates);
  },

  removeContact: async (id) => {
    set((state) => ({ network: state.network.filter((n) => n.id !== id) }));
    await deleteRow('network', id);
  },

  // ─── Health & Fitness Actions ───────────────────────────────────────────
  addWeightEntry: async (entry) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ weightLog: [...state.weightLog, { ...entry, id: tempId }] }));
    const row = await insertRow('weight_log', entry);
    if (row) {
      set((state) => ({ weightLog: state.weightLog.map((w) => (w.id === tempId ? { ...w, id: row.id } : w)) }));
    }
  },

  addNutritionEntry: async (entry) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ nutritionLog: [...state.nutritionLog, { ...entry, id: tempId }] }));
    const row = await insertRow('nutrition_log', entry);
    if (row) {
      set((state) => ({ nutritionLog: state.nutritionLog.map((n) => (n.id === tempId ? { ...n, id: row.id } : n)) }));
    }
  },

  addWorkout: async (workout) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ workoutLog: [...state.workoutLog, { ...workout, id: tempId }] }));
    const row = await insertRow('workout_log', workout);
    if (row) {
      set((state) => ({ workoutLog: state.workoutLog.map((w) => (w.id === tempId ? { ...w, id: row.id } : w)) }));
    }
  },

  updateLiftTarget: async (id, updates) => {
    set((state) => ({ liftTargets: state.liftTargets.map((l) => (l.id === id ? { ...l, ...updates } : l)) }));
    await updateRow('lift_targets', id, updates);
  },

  updateCalorieTargets: async (updates) => {
    set((state) => ({ calorieTargets: { ...state.calorieTargets, ...updates } }));
    await upsertConfig('calorie_targets', { ...get().calorieTargets, ...updates });
  },

  // Generic health table seeder — batch upserts in chunks of 100
  seedHealthTable: async (tableName, rows, conflictColumns = 'user_id,date') => {
    const userId = (await supabase.auth.getUser()).data.user?.id;
    if (!userId) return;
    const snakeRows = rows.map((r) => ({ ...toSnakeCase(r), user_id: userId }));
    for (let i = 0; i < snakeRows.length; i += 100) {
      const batch = snakeRows.slice(i, i + 100);
      const { error } = await supabase
        .from(tableName)
        .upsert(batch, { onConflict: conflictColumns });
      if (error) console.error(`Seed ${tableName} error (batch ${i}):`, error.message);
    }
  },

  addBodyMetric: async (metric) => {
    const tempId = crypto.randomUUID();
    set((state) => ({ bodyMetrics: [...state.bodyMetrics, { ...metric, id: tempId }] }));
    const row = await insertRow('body_metrics', metric);
    if (row) {
      set((state) => ({ bodyMetrics: state.bodyMetrics.map((m) => (m.id === tempId ? { ...m, id: row.id } : m)) }));
    }
  },
}));

export default useStore;
